import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ThemingService } from 'src/app/theming.service';
import { SettingsService } from 'src/app/settings.service';
import { LambdaService } from 'src/app/lambda.service';
import { Router } from '@angular/router';
import { TimekitTabsComponent } from '../timekit-tabs/timekit-tabs.component';
import { ApiService } from 'src/app/api.service';
import { ChangeDetectorRef } from '@angular/core';

declare var $: any;

interface Project {
  projectId: string;
  projectName: string;
  externalId: string;
  timekitProjectId: number;
  timekitResourceId?: string;
}

interface Resource {
  timekitResourceId: number;
  shopId: number;
  resourceId: string;
  resourceName: string;
}

@Component({
  selector: 'app-timekit-projects',
  templateUrl: './timekit-projects.component.html',
  styleUrls: ['./timekit-projects.component.scss']
})

export class TimekitProjectsComponent implements OnInit {
  @Input() data: any;
  @Output() edit = new EventEmitter<string>();
  projects: Project[] = [];
  loading: boolean = false;
  showNoProjectsMessage: boolean = false;
  externalId: string = '';
  timekitProjectId: number = 0;
  @Input() goBackCallback!: () => void

  selectedProject: Project | null = null;
  projectResources: Resource[] = [];
  loadingResources: boolean = false;
  allResources: any[] = [];

  showStatusModal = false;
  statusModalType: 'loading' | 'success' = 'loading';
  statusModalMessage = '';

  constructor(
    public themingService: ThemingService,
    private settingsService: SettingsService,
    private lambdaService: LambdaService,
    private router: Router,
    private tabsParent: TimekitTabsComponent,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.getTimekitProjects()
    this.settingsService.getListingProjects$().subscribe((active) => {
      if (active) {
        this.getTimekitProjects();
      }
    });
  }

  async getTimekitProjects() {
    this.loading = true;
    this.showNoProjectsMessage = false;

    try {
      const response = await this.lambdaService.getTimekitProjects(this.themingService.shopId) as { data: Project[] };

      this.projects = response.data || [];
    } catch (error) {
      console.error('Error getting projects:', error);
      this.projects = [];
    } finally {
      this.loading = false;
    }
  }

  editProject(externalId: string) {
    this.tabsParent.setCreateProjectTab(externalId);
  }

  setCreatingProjects() {
    this.settingsService.setCreatingProjects();
  }

  async viewResources(project: Project) {
    this.selectedProject = project;
    this.loadingResources = true;

    try {
      const response = await this.lambdaService.getTimekitResources(this.themingService.shopId) as { data: Resource[] };
      this.allResources = response.data || [];

      if (project.timekitResourceId) {
        const timekitResourceIds = project.timekitResourceId.split(',').map(id => parseInt(id.trim(), 10));
        this.projectResources = this.allResources.filter(
          resource => timekitResourceIds.includes(resource.timekitResourceId)
        );
      } else {
        this.projectResources = [];
      }

      $('#resourcesModal').modal('show');
    } catch (error) {
      console.error('Error getting resources:', error);
      this.allResources = [];
      this.projectResources = [];
    } finally {
      this.loadingResources = false;
    }
  }

  isResourceLinked(resource: Resource): boolean {
    return this.projectResources.some(
      linked => linked.resourceId === resource.resourceId
    );
  }

  private handleResourceSuccess(message: string) {
    this.statusModalType = 'success';
    this.statusModalMessage = message;
    if (this.selectedProject) {
      this.viewResources(this.selectedProject);
    }
    setTimeout(() => {
      this.showStatusModal = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  addResourceToProject(resource: Resource) {
    if (!this.selectedProject) return;

    this.statusModalType = 'loading';
    this.statusModalMessage = '¡Agregando recurso!';
    this.showStatusModal = true;

    this.apiService.addResourceToProject(
      this.themingService.shopId,
      this.selectedProject.projectId,
      resource.resourceId,
      resource.timekitResourceId
    )
      .then((response: any) => {
        if (response && response.error && response.error.message) {
          console.error('Error al agregar el recurso:', response.error.message);
          this.closeStatusModalWithError('Ha ocurrido un error al agregar el recurso: ' + response.error.message);
        } else {
          this.handleResourceSuccess('Recurso agregado con éxito');
        }
      })
      .catch(error => {
        console.error('Error adding resource:', error);
        this.closeStatusModalWithError('Ha ocurrido un error al agregar el recurso');
      });
  }

  closeStatusModalWithError(msg: string) {
    this.statusModalType = 'success';
    this.statusModalMessage = msg;
    setTimeout(() => {
      this.showStatusModal = false;
      this.cdr.detectChanges();
    }, 2500);
  }

  deleteResource(resourceId: string, timekitResourceId: number) {
    if (!this.selectedProject) return;

    this.statusModalType = 'loading';
    this.statusModalMessage = '¡Eliminando recurso!';
    this.showStatusModal = true;

    this.apiService.deleteResourceFromProject(
      this.themingService.shopId,
      this.selectedProject.projectId,
      resourceId,
      timekitResourceId
    )
      .then((response: any) => {
        if (response && response.error && response.error.message) {
          console.error('Error deleting resource:', response.error.message);
          this.closeStatusModalWithError('Ha ocurrido un error al eliminar el recurso: ' + response.error.message);
        } else {
          this.handleResourceSuccess('Recurso eliminado con éxito');
        }
      })
      .catch(error => {
        console.error('Error deleting resource:', error);
        this.closeStatusModalWithError('Ha ocurrido un error al eliminar el recurso');
      });
  }

  deleteProject(projectId: string, timekitProjectId: number) {

    this.statusModalType = 'loading';
    this.statusModalMessage = '¡Eliminando proyecto!';
    this.showStatusModal = true;

    this.apiService.deleteTimekitProject(projectId, timekitProjectId, this.themingService.shopId)
      .then(() => {
        this.getTimekitProjects();
        this.handleResourceSuccess('Proyecto eliminado con éxito');
      })
      .catch(error => {
        this.closeStatusModalWithError('Ha ocurrido un error al eliminar el proyecto');
        console.error('Error deleting project:', error);
      });
  }

  closeResourcesModal() {
    $('#resourcesModal').modal('hide');
    this.selectedProject = null;
    this.projectResources = [];
  }

  onModalClosed(): void {
    this.showStatusModal = false;
  }
}
